import {
  AesKwKeyWrapper,
  base64UrlToBytes,
  bytesToBase64Url,
  generateDek,
  type PlatformKeyStore,
} from '@signbento/crypto'
import { createVaultCryptoBox } from '../crypto-box.js'
import { StorageError } from '../errors.js'
import { runMigrations, VAULT_DATA_MIGRATIONS } from '../migrations/runner.js'
import type { LocalVault, VaultChangeChannel, VaultClock } from '../types.js'
import { createLocalVault } from '../vault-core.js'
import { createBroadcastVaultChangeChannel } from './change-channel.js'
import { IdbRecordStore, VAULT_DB_NAME } from './idb-record-store.js'
import { IdbMigrationStateStore } from './idb-migration-state.js'
import { getNavigatorOpfsRoot, OpfsBlobStore, type OpfsDirectoryHandle } from './opfs-blob-store.js'
import { WebPlatformKeyStore } from './web-key-store.js'

/**
 * Web vault composition root: IndexedDB records + OPFS blobs, encrypted with a
 * DEK that is wrapped by a non-extractable platform KEK (PRD §8.5, §11.2).
 * `opfs_root` is injectable so tests can run the real adapter over the OPFS shim.
 */

export const DEK_PROFILE_V1 = 'signbento-vault-dek-v1'

interface WrappedDekRecord {
  readonly profile: string
  readonly key_id: string
  readonly wrapped: string
}

export interface OpenWebVaultOptions {
  readonly vault_id: string
  readonly clock: VaultClock
  readonly database_name?: string
  readonly key_id?: string
  readonly key_store?: PlatformKeyStore
  readonly opfs_root?: OpfsDirectoryHandle
  readonly opfs_directory?: string
  readonly channel?: VaultChangeChannel | null
}

export interface WebVaultHandle {
  readonly vault: LocalVault
  readonly key_id: string
  close(): Promise<void>
}

export async function openWebVault(options: OpenWebVaultOptions): Promise<WebVaultHandle> {
  // One database and blob directory per vault by default, so two vault ids can
  // never read each other's ciphertext (records/blobs are not cross-namespaced).
  const databaseName = options.database_name ?? `${VAULT_DB_NAME}:${options.vault_id}`
  const keyId = options.key_id ?? `vault:${options.vault_id}`
  const records = await IdbRecordStore.open(databaseName)
  const db = records.database

  try {
    const keyStore = options.key_store ?? new WebPlatformKeyStore(db)
    const kek = await keyStore.getOrCreateKek(keyId)
    const dek = await resolveDek(db, kek, options.vault_id, keyId)
    const crypto = createVaultCryptoBox({ dek, key_id: keyId, vault_id: options.vault_id })

    await runMigrations(new IdbMigrationStateStore(db), VAULT_DATA_MIGRATIONS)

    const opfsRoot = options.opfs_root ?? getNavigatorOpfsRoot()
    const blobs = new OpfsBlobStore(opfsRoot, options.opfs_directory ?? `blobs:${options.vault_id}`)
    const channel =
      options.channel ?? createBroadcastVaultChangeChannel(`signbento-vault:${options.vault_id}`)

    const vault = createLocalVault({
      vault_id: options.vault_id,
      records,
      blobs,
      crypto,
      clock: options.clock,
      channel,
    })

    return {
      vault,
      key_id: keyId,
      close: async () => {
        channel.close()
        records.close()
      },
    }
  } catch (error) {
    records.close()
    throw error
  }
}

async function resolveDek(
  db: IdbRecordStore['database'],
  kek: CryptoKey,
  vaultId: string,
  keyId: string,
): Promise<CryptoKey> {
  const metaKey = `dek:${vaultId}`
  const existing = await db.get('meta', metaKey)
  if (existing !== undefined && existing !== null) {
    return unwrapStoredDek(existing, kek, vaultId)
  }

  const dek = await generateDek()
  const wrapped = await new AesKwKeyWrapper().wrap(dek, kek)
  const record: WrappedDekRecord = {
    profile: DEK_PROFILE_V1,
    key_id: keyId,
    wrapped: bytesToBase64Url(wrapped),
  }
  try {
    // A second opener may reach this point at the same time. Only one generated DEK may become
    // the vault's DEK; the losing opener must decrypt the winner's record before it can write.
    await db.add('meta', record, metaKey)
    return dek
  } catch (error) {
    if (!(error instanceof Error) || error.name !== 'ConstraintError') throw error
    return unwrapStoredDek(await db.get('meta', metaKey), kek, vaultId)
  }
}

async function unwrapStoredDek(
  stored: unknown,
  kek: CryptoKey,
  vaultId: string,
): Promise<CryptoKey> {
  const record = stored as Partial<WrappedDekRecord> | null | undefined
  if (record?.profile !== DEK_PROFILE_V1 || typeof record.wrapped !== 'string') {
    throw new StorageError(
      'OP_STORAGE_CORRUPT_REVISION',
      `wrapped DEK for vault ${vaultId} is malformed`,
    )
  }
  try {
    return await new AesKwKeyWrapper().unwrapDek(base64UrlToBytes(record.wrapped), kek)
  } catch {
    throw new StorageError(
      'OP_STORAGE_CORRUPT_REVISION',
      `wrapped DEK for vault ${vaultId} cannot be unwrapped with this platform key`,
    )
  }
}
